import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { ChecklistTemplate } from 'src/app/components/checklists/checklists';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { utils } from 'src/app/modules/libs/utils';
import { MultipleStatusChangeOption } from '../../cl-multiple-status-change/cl-multiple-status-change.component';

@Component({
  selector: 'cl-editor-multiple-status-change',
  templateUrl: './cl-editor-multiple-status-change.component.html',
  styleUrls: ['./cl-editor-multiple-status-change.component.scss']
})
export class ClEditorMultipleStatusChangeComponent implements OnInit, OnDestroy {

  @Input() disabled!: boolean;
  @Input() configuration?: string;
  @Input() checklistTemplate!: ChecklistTemplate;

  @Output() changed = new EventEmitter<any>();

  options: MultipleStatusChangeOption[] = [];
  roles?: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  constructor(private store: Store) { }

  ngOnInit(): void {
    this.loadRoles();
    this.loadConfiguration();
  }

  ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
  }

  /**
   * Loads roles so configured role IDs can be shown as chips per status option.
   */
  private loadRoles(): void {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      this.roles = data;
    });
  }

  /**
   * Reads the current multiple status-change configuration.
   */
  private loadConfiguration(): void {
    const configuration = utils.JSONparse(this.configuration);
    this.options = (configuration?.options ?? []).map((option: MultipleStatusChangeOption, index: number) => ({
      ...option,
      id: option.id ?? index + 1,
      roles: option.roles ?? []
    }));
    if (!this.options.length)
      this.addOption(false);
  }

  /**
   * Adds a new status-change radio option.
   * @param emitChange Whether to immediately emit the new configuration.
   */
  addOption(emitChange: boolean = true): void {
    const nextId = Math.max(0, ...this.options.map(option => option.id ?? 0)) + 1;
    this.options.push({ id: nextId, label: '', status: null, roles: [] });
    if (emitChange)
      this.setConfiguration();
  }

  /**
   * Removes one configured status-change option.
   * @param option Option to remove.
   */
  removeOption(option: MultipleStatusChangeOption): void {
    this.options = this.options.filter(currentOption => currentOption.id !== option.id);
    this.setConfiguration();
  }

  /**
   * Updates the target status for an option.
   * @param option Option being edited.
   * @param statusID Selected ChecklistExecStatus ID.
   */
  statusSelected(option: MultipleStatusChangeOption, statusID: number | null): void {
    option.status = statusID;
    this.setConfiguration();
  }

  /**
   * Updates notification roles for an option.
   * @param option Option being edited.
   * @param selectedRoles Selected role records.
   */
  rolesSelected(option: MultipleStatusChangeOption, selectedRoles: Role[]): void {
    option.roles = selectedRoles?.map(role => role.id) ?? [];
    this.setConfiguration();
  }

  /**
   * Returns role records for the configured role IDs on an option.
   * @param option Option whose roles should be displayed.
   * @returns Matching roles from the role store.
   */
  getSelectedRoles(option: MultipleStatusChangeOption): Role[] {
    return this.roles?.filter(role => option.roles?.includes(role.id)) ?? [];
  }

  /**
   * Builds the small configuration object expected by cl-editor-status.
   * @param option Option whose status selector is being rendered.
   * @returns Serialized status selector configuration.
   */
  getStatusConfiguration(option: MultipleStatusChangeOption): string {
    return utils.JSONstringify({ status: option.status });
  }

  /**
   * Emits the full multiple status-change configuration.
   */
  setConfiguration(): void {
    this.changed.emit({ options: this.options });
  }
}
